import React, { useMemo } from "react";
import { LineChart } from "@tremor/react";
import { Alert, Card, Space, Typography } from "antd";
import dayjs from "dayjs";
import isEmpty from "lodash.isempty";
import mean from "lodash.mean";
import round from "lodash.round";

import { MeasurementType } from "@fitness-app/data-models/entities/Measurement";
import { getErrorMessage } from "@fitness-app/utils";

import { useMeasurementsForRange } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/hooks/useMeasurementsForRange";
import { type CommonChartProps } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/types";

type WeightMeasurementChartProps = CommonChartProps;

const valueFormatterAbsolute = (number: number) => `${Intl.NumberFormat("pl").format(number).toString()} kg`;

const WeightMeasurementChart = ({ range }: WeightMeasurementChartProps) => {
  const {
    data: measurements = [],
    isInitialLoading,
    isError,
    error,
  } = useMeasurementsForRange(MeasurementType.WeightMeasurement, range);

  const chartData: { date: string; Waga: number }[] = useMemo(() => {
    return measurements
      .filter((measurement) => typeof measurement.data.weight === "number")
      .map((measurement) => ({
        date: dayjs(measurement.eventTimestamp).format("DD.MM.YYYY"),
        Waga: measurement.data.weight,
      }));
  }, [measurements]);

  const averageWeight = round(mean(chartData.map((item) => item.Waga)), 1);

  return (
    <Card
      title="Waga"
      type="inner"
      className="relative"
      loading={isInitialLoading}
      extra={
        <Space direction="horizontal">
          <Typography.Text strong>
            Średnia z wybranego okresu: {isEmpty(chartData) ? "-" : `${averageWeight}kg`}
          </Typography.Text>
        </Space>
      }
    >
      <LineChart
        className="mt-8 h-80"
        data={chartData}
        index="date"
        categories={["Waga"]}
        colors={["blue"]}
        showLegend={false}
        valueFormatter={valueFormatterAbsolute}
        yAxisWidth={60}
        curveType="monotone"
        autoMinValue
        intervalType={"preserveStartEnd"}
        allowDecimals={false}
      />

      {isError && (
        <Alert
          message={getErrorMessage(error) || "Wystąpił błąd podczas pobierania danych"}
          type="error"
          showIcon
          className="absolute left-10 right-10 top-[55%]"
        />
      )}
      {isEmpty(chartData) && !isError && (
        <Alert
          message="Brak informacji na temat wagi klienta"
          type="warning"
          showIcon
          className="absolute left-10 right-10 top-[55%]"
        />
      )}
    </Card>
  );
};

export default WeightMeasurementChart;
